import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useMonetization } from '../contexts/MonetizationContext';
import { useLocalHistory } from '../hooks/useLocalHistory';
import DisButton from '../components/design-system/DisButton';
import DisCard from '../components/design-system/DisCard';
import DisAvatar from '../components/design-system/DisAvatar';
import AddChildModal from '../components/AddChildModal';
import ChildrenManagementModal from '../components/ChildrenManagementModal';
import MonetizationPopup from '../components/MonetizationPopup';
import FeedbackButtons from '../components/FeedbackButtons';
import ThinkingPopup from '../components/ThinkingPopup';
import PWAInstallPrompt from '../components/PWAInstallPrompt';
import ReassuringChildPrompt from '../components/ReassuringChildPrompt';
import LoadingQuestionModal from '../components/LoadingQuestionModal';
import AdaptiveLevelService from '../services/AdaptiveLevelService';
import HistoryService from '../services/HistoryService';
import Footer from '../components/Footer';
import FloatingMenu from '../components/FloatingMenu';
import GamificationHeader from '../components/GamificationHeader';
import StreakDisplay from '../components/StreakDisplay';
import WeeklyChallengeDisplay from '../components/WeeklyChallengeDisplay';
import WeeklyRecapModal from '../components/WeeklyRecapModal';
import StreakMilestoneModal from '../components/StreakMilestoneModal';
import BadgeUnlockModal from '../components/BadgeUnlockModal';
import GamificationService from '../services/GamificationService';
import LibraryView from '../components/LibraryView';
import { trackEvent, EVENTS } from '../services/analytics';

const MASCOT_URL = 'https://customer-assets.emergentagent.com/job_7b59aa73-04f7-469e-b3b0-3b5db031400b/artifacts/0zxv9crx_02D61451-5FF5-40EF-9C50-17F42BA59024.png';

const DemoChildPopup = ({ childName, onSubmit, onClose }) => {
  const [name, setName] = useState(childName || '');
  const currentYear = new Date().getFullYear();
  const [birthMonth, setBirthMonth] = useState(1);
  const [birthYear, setBirthYear] = useState(currentYear - 5);
  const [gender, setGender] = useState('non_specifie');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const months = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];

  const calcAge = () => {
    const now = new Date();
    let a = now.getFullYear() - birthYear;
    if (now.getMonth() + 1 < birthMonth) a--;
    return Math.max(1, a);
  };

  const handleSubmit = async () => {
    if (!name.trim()) { setError('Le prénom est requis'); return; }
    setLoading(true);
    setError('');
    try {
      await onSubmit({
        name: name.trim(),
        age: calcAge(),
        gender,
        birth_month: parseInt(birthMonth),
        birth_year: parseInt(birthYear)
      });
    } catch (e) {
      setError('Erreur lors de la création. Réessayez.');
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4" style={{ background: 'rgba(0,0,0,0.5)' }}>
      <div className="bg-white rounded-t-3xl sm:rounded-3xl p-6 shadow-2xl w-full sm:max-w-sm relative z-[61]">
        <div className="flex items-center gap-3 mb-4">
          <img src={MASCOT_URL} alt="Alex" className="w-12 h-12 object-contain" />
          <p className="text-gray-700 text-sm leading-relaxed">
            Pour proposer une réponse que <strong>{name || 'votre enfant'}</strong> comprenne, quel est son âge ?
          </p>
        </div>

        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date de naissance</label>
            <div className="flex gap-2">
              <select
                value={birthMonth}
                onChange={e => setBirthMonth(e.target.value)}
                className="flex-1 px-3 py-3 rounded-xl border-2 border-gray-200 focus:border-[#5BBFBD] focus:outline-none bg-white text-sm"
                data-testid="demo-popup-month"
              >
                {months.map((m, i) => (
                  <option key={i} value={i + 1}>{m}</option>
                ))}
              </select>
              <select
                value={birthYear}
                onChange={e => setBirthYear(e.target.value)}
                className="w-28 px-3 py-3 rounded-xl border-2 border-gray-200 focus:border-[#5BBFBD] focus:outline-none bg-white text-sm"
                data-testid="demo-popup-year"
              >
                {Array.from({ length: 12 }, (_, i) => currentYear - 2 - i).map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Genre (optionnel)</label>
            <div className="flex gap-2">
              {[
                { v: 'garcon', l: '👦 Garçon' },
                { v: 'fille', l: '👧 Fille' },
                { v: 'non_specifie', l: '🌟 Autre' }
              ].map(g => (
                <button
                  key={g.v}
                  type="button"
                  onClick={() => setGender(g.v)}
                  className={`flex-1 py-2 rounded-xl text-sm font-medium transition-all ${
                    gender === g.v
                      ? 'bg-[#5BBFBD] text-white shadow-md'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  data-testid={`demo-popup-gender-${g.v}`}
                >
                  {g.l}
                </button>
              ))}
            </div>
          </div>
        </div>

        {error && <p className="text-red-500 text-sm mt-2">{error}</p>}

        <button
          onClick={handleSubmit}
          disabled={loading}
          className="w-full mt-4 py-3 rounded-xl font-bold text-white shadow-md transition-all hover:opacity-90 active:scale-[0.98] disabled:opacity-50"
          style={{ background: 'linear-gradient(135deg, #4A90D9 0%, #5BBFBD 100%)' }}
          data-testid="demo-popup-submit"
        >
          {loading ? 'Création en cours...' : `Voir la réponse pour ${name || 'votre enfant'} 🌟`}
        </button>

        <button
          onClick={onClose}
          className="w-full mt-2 py-2 text-sm text-gray-500 hover:text-gray-700"
        >
          Plus tard
        </button>
      </div>
    </div>
  );
};

const ChatPage = ({ onNavigateToHistory, onNavigateToSubscription, onNavigateToAdmin, onNavigateToStory, currentMode = 'chat' }) => {
  const { user, api, logout } = useAuth();
  const { canAskQuestion, canAskQuestionAsync, questionsLeft, isPremium, getUpgradeMessage, checkAccess } = useMonetization();
  const { saveConversation } = useLocalHistory();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [children, setChildren] = useState([]);
  const [selectedChild, setSelectedChild] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showAddChildModal, setShowAddChildModal] = useState(false);
  const [showChildSelection, setShowChildSelection] = useState(false);
  const [showMonetizationPopup, setShowMonetizationPopup] = useState(false);
  const [showThinkingPopup, setShowThinkingPopup] = useState(false);
  const [showReassuringPrompt, setShowReassuringPrompt] = useState(false);
  const [hasCheckedChildren, setHasCheckedChildren] = useState(false);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(''); // Pour le modal de chargement
  const [demoData, setDemoData] = useState(null);
  const [showDemoChildPopup, setShowDemoChildPopup] = useState(false);
  const [demoSaved, setDemoSaved] = useState(false);
  // Gamification modals
  const [weeklyRecap, setWeeklyRecap] = useState(null);
  const [streakMilestone, setStreakMilestone] = useState(null);
  const [badgeUnlock, setBadgeUnlock] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeChildName, setUpgradeChildName] = useState('');
  const [pendingChildName, setPendingChildName] = useState('');
  const [viewMode, setViewMode] = useState('chat'); // 'chat' or 'library'
  
  const messagesEndRef = useRef(null);
  const adaptiveService = AdaptiveLevelService.getInstance();
  const historyService = HistoryService.getInstance();

  // Ouvrir le modal des enfants si ?children=true est présent dans l'URL
  useEffect(() => {
    if (searchParams.get('children') === 'true') {
      setShowChildSelection(true);
      // Nettoyer le paramètre de l'URL
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    fetchChildren();
    // Check weekly recap on mount
    const checkRecap = async () => {
      if (!api) return;
      const svc = GamificationService.getInstance();
      svc.setApi(api);
      const data = await svc.getWeeklyRecap();
      if (data.recap) setWeeklyRecap(data.recap);
    };
    checkRecap();
  }, []);

  // Charger les données de la démo landing page si elles existent
  useEffect(() => {
    const pendingQuestion = localStorage.getItem('dismaman_pending_question');
    const pendingResponse = localStorage.getItem('dismaman_pending_response');
    const pendingChildName = localStorage.getItem('dismaman_pending_child_name');

    if (pendingQuestion && pendingResponse && !demoSaved) {
      const data = {
        question: pendingQuestion,
        response: pendingResponse,
        childName: pendingChildName || '',
        quiz: null
      };
      try {
        const quizStr = localStorage.getItem('dismaman_pending_quiz');
        if (quizStr) data.quiz = JSON.parse(quizStr);
      } catch (e) {}

      setDemoData(data);
    }
  }, [demoSaved]);

  // Effet pour vérifier s'il faut afficher le popup (avec temporisation anti-flash)
  useEffect(() => {
    if (isLoading || hasCheckedChildren) return;
    
    const timer = setTimeout(() => {
      setHasCheckedChildren(true);
      
      // Si données de démo et pas d'enfant → pré-remplir le prénom
      if (children.length === 0 && demoData) {
        setPendingChildName(demoData.childName || '');
      }
      // Pour inscription directe (sans démo, sans enfant) → l'interface inline gère tout
      // Pas de popup bloquant !
    }, 500);
    
    return () => clearTimeout(timer);
  }, [isLoading, children.length, hasCheckedChildren, demoData]);
  
  // Effet pour reset le pendingChildName quand des enfants sont ajoutés
  useEffect(() => {
    if (children.length > 0 && showReassuringPrompt) {
      setShowReassuringPrompt(false);
    }
  }, [children.length, showReassuringPrompt]);

  // Scroll vers le haut UNIQUEMENT quand une nouvelle question est posée (pas les feedbacks)
  const [lastQuestionCount, setLastQuestionCount] = useState(0);
  
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Ne scroller que quand le nombre de questions utilisateur change (pas les réponses IA/feedback)
  useEffect(() => {
    if (messages.length > 0 && selectedChild) {
      const userMessageCount = messages.filter(m => m.role === 'user' && !m.isFollowUp && !m.isThankYou).length;
      if (userMessageCount > lastQuestionCount) {
        setLastQuestionCount(userMessageCount);
        scrollToTop();
      }
    }
  }, [messages.length, selectedChild, lastQuestionCount]);

  const fetchChildren = async () => {
    try {
      const response = await api.get('/children');
      setChildren(response.data.children);
    } catch (error) {
      console.error('Error fetching children:', error);
    }
  };

  const handleAddChild = async (childData) => {
    try {
      const response = await api.post('/children', childData);
      await fetchChildren();
      return response.data;
    } catch (error) {
      console.error('Error adding child:', error);
      
      // Vérifier si c'est une erreur de limite de monétisation
      if (error.response?.status === 400 || error.response?.status === 429) {
        // Ouvrir le popup de monétisation au lieu d'afficher l'erreur
        setShowMonetizationPopup(true);
        throw new Error('Limite de profils enfants atteinte - popup affiché');
      }
      
      throw error;
    }
  };

  const handleChildCreatedFromPrompt = (child) => {
    fetchChildren();
  };

  // Gestion de la création d'enfant depuis le popup (premier flow ou démo)
  const handleDemoChildCreated = async (childData) => {
    try {
      const response = await api.post('/children', {
        name: childData.name,
        age: childData.age,
        birth_month: childData.birth_month,
        birth_year: childData.birth_year,
        gender: childData.gender
      });

      const createdChild = response.data.child || response.data;
      await fetchChildren();
      setShowDemoChildPopup(false);

      // Sauvegarder la conversation de la démo dans l'historique (cadeau, ne décompte pas du quota)
      if (demoData && !demoSaved) {
        try {
          await api.post('/demo/save-to-history', {
            question: demoData.question,
            response: demoData.response,
            child_name: createdChild.name || demoData.childName,
            child_id: createdChild.id
          });
        } catch (e) {
          console.error('Erreur sauvegarde démo:', e);
        }

        // Nettoyer localStorage
        localStorage.removeItem('dismaman_pending_question');
        localStorage.removeItem('dismaman_pending_response');
        localStorage.removeItem('dismaman_pending_child_name');
        localStorage.removeItem('dismaman_pending_quiz');
        setDemoSaved(true);
      }

      // Si une question est en attente, l'envoyer automatiquement
      if (inputMessage.trim()) {
        // Petit délai pour que le state children soit mis à jour
        setTimeout(() => {
          handleAskForChild(createdChild);
        }, 100);
      }

      return createdChild;
    } catch (error) {
      console.error('Erreur création enfant:', error);
      throw error;
    }
  };

  const handleDeleteChild = async (childId) => {
    try {
      await api.delete(`/children/${childId}`);
      await fetchChildren();
      
      // Si l'enfant supprimé était sélectionné, le désélectionner
      if (selectedChild && selectedChild.id === childId) {
        setSelectedChild(null);
        setMessages([]);
      }
      
      return true;
    } catch (error) {
      console.error('Error deleting child:', error);
      
      if (error.response?.status === 404) {
        alert('❌ Profil enfant non trouvé');
      } else {
        alert('❌ Erreur lors de la suppression. Veuillez réessayer.');
      }
      
      throw error;
    }
  };

  // NOUVELLE LOGIQUE : Gérer la question depuis la page d'accueil
  const handleAskForChild = async (child) => {
    if (!inputMessage.trim()) {
      alert('📝 Veuillez d\'abord écrire votre question !');
      return;
    }

    // Vérification des limites de monétisation via backend
    const canAsk = await canAskQuestionAsync();
    if (!canAsk) {
      setUpgradeChildName(child.name);
      setShowUpgradeModal(true);
      return;
    }

    // Sélectionner automatiquement l'enfant et démarrer une nouvelle conversation
    setSelectedChild(child);
    
    // Stocker la question pour le modal de chargement
    setCurrentQuestion(inputMessage.trim());
    
    const userMessage = {
      id: Date.now(),
      role: 'user',
      content: inputMessage.trim(),
      timestamp: new Date().toISOString(),
      showFeedback: false
    };

    // Démarrer une nouvelle conversation : vider les messages précédents
    setMessages([userMessage]);
    
    // Créer une nouvelle session d'historique pour ce nouvel enfant
    const newSessionId = historyService.startConversationSession(child.id, child.name);
    setCurrentSessionId(newSessionId);
    
    setShowThinkingPopup(true);
    setIsLoading(true);

    try {
      // Génère une réponse adaptée avec le service d'apprentissage
      const aiParams = adaptiveService.getAIParameters(child.id, child.age);
      console.log(`🎯 Paramètres adaptatifs pour ${child.name}:`, aiParams);

      const response = await api.post('/chat', {
        message: inputMessage.trim(),
        child_id: child.id
      });

      // Ferme le popup de réflexion
      setShowThinkingPopup(false);

      const assistantMessage = {
        id: Date.now() + 1,
        role: 'assistant',
        content: response.data.response,
        timestamp: new Date().toISOString(),
        showFeedback: true,
        messageId: response.data.conversation_id,
        complexityLevel: aiParams.complexityLevel
      };

      setMessages(prev => [...prev, assistantMessage]);

      // Mettre à jour le sessionId avec le vrai conversation_id du backend (nécessaire pour le quiz)
      if (response.data.conversation_id) {
        setCurrentSessionId(response.data.conversation_id);
      }

      // Sauvegarde dans l'historique avec service
      await historyService.addMessage(
        newSessionId || response.data.conversation_id,
        inputMessage.trim(),
        response.data.response,
        aiParams.complexityLevel,
        'general'
      );

      // Déclencher le prompt PWA après la première réponse IA réussie
      if (!localStorage.getItem('dismaman_first_success')) {
        localStorage.setItem('dismaman_first_success', 'true');
        window.dispatchEvent(new CustomEvent('dismaman:first-success'));
      }

      // Handle gamification response (streak milestones, daily stars)
      if (response.data.gamification) {
        const gam = response.data.gamification;
        if (gam.stars_bonus > 0) {
          window.dispatchEvent(new CustomEvent('stars-earned', {
            detail: { amount: gam.stars_bonus, newBalance: gam.stars_bonus }
          }));
        }
        if (gam.milestone) {
          setTimeout(() => setStreakMilestone(gam.milestone), 1500);
        }
      }


      // Vide la zone de texte
      setInputMessage('');

    } catch (error) {
      console.error('Error sending message:', error);
      setShowThinkingPopup(false);
      
      if (error.response?.status === 403) {
        setShowMonetizationPopup(true);
      }
      // Suppression du message d'erreur fantôme - ne rien afficher si l'API échoue
    } finally {
      setIsLoading(false);
    }
  };

  // Fonction pour la sélection normale d'enfant (modal)
  const handleSelectChild = (child) => {
    setSelectedChild(child);
    setShowChildSelection(false);
    setMessages([{
      id: Date.now(),
      role: 'assistant',
      content: `Salut ${child.name} ! 👋 Je suis ton assistant IA magique ! ✨ Pose-moi toutes tes questions, j'adore expliquer les mystères du monde ! 🌍🔍 Tu es prêt(e) pour l'aventure ? 🚀`,
      timestamp: new Date().toISOString(),
      showFeedback: false
    }]);
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || !selectedChild || isLoading) return;

    // Sauvegarder le message avant de vider la zone de texte
    const messageToSend = inputMessage.trim();
    
    // Stocker la question pour le modal de chargement
    setCurrentQuestion(messageToSend);

    // Vérification des limites de monétisation via backend
    const canAsk = await canAskQuestionAsync();
    if (!canAsk) {
      setShowMonetizationModal(true); // Afficher le modal au lieu d'une alerte
      return;
    }

    // Afficher le popup de chargement
    setShowThinkingPopup(true);

    const userMessage = {
      id: Date.now(),
      role: 'user',
      content: messageToSend,
      timestamp: new Date().toISOString()
    };

    // Déterminer si c'est une question de suivi (conversation en cours) ou une nouvelle conversation
    // On considère que c'est une question de suivi s'il y a déjà des messages Q&A (plus que le message de bienvenue)
    const hasExistingConversation = messages.length > 1 && messages.some(m => m.role === 'user');
    const existingConversationId = hasExistingConversation 
      ? messages.find(m => m.messageId)?.messageId 
      : null;
    
    // Si conversation en cours, ajouter le message. Sinon, commencer nouvelle conversation
    if (hasExistingConversation) {
      setMessages(prev => [...prev, userMessage]);
    } else {
      setMessages([userMessage]);
      // Créer une nouvelle session d'historique seulement pour nouvelle conversation
      const newSessionId = historyService.startConversationSession(selectedChild.id, selectedChild.name);
      setCurrentSessionId(newSessionId);
    }
    
    // Vider la zone de texte IMMÉDIATEMENT après avoir sauvegardé le message
    setInputMessage('');
    setIsLoading(true);

    try {
      // Génère une réponse adaptée avec le service d'apprentissage
      const aiParams = adaptiveService.getAIParameters(selectedChild.id, selectedChild.age);
      console.log(`🎯 Paramètres adaptatifs pour ${selectedChild.name}:`, aiParams);

      // Préparer l'historique de conversation pour le contexte
      // Filtrer pour garder seulement les Q&A (pas le message de bienvenue)
      const conversationHistory = hasExistingConversation
        ? messages
            .filter(m => m.role === 'user' || (m.role === 'assistant' && m.showFeedback))
            .map(m => ({ role: m.role, content: m.content }))
        : [];

      // Utilisation du service IA backend avec contexte de conversation
      const response = await api.post('/chat', {
        message: messageToSend,
        child_id: selectedChild.id,
        conversation_id: existingConversationId,
        continue_conversation: hasExistingConversation,
        conversation_history: conversationHistory // Nouvel attribut pour le contexte
      });

      // Fermer le popup de chargement
      setShowThinkingPopup(false);

      const assistantMessage = {
        id: Date.now() + 1,
        role: 'assistant',
        content: response.data.response,
        timestamp: new Date().toISOString(),
        showFeedback: true, // Montre les boutons de feedback pour les réponses IA
        messageId: response.data.conversation_id,
        complexityLevel: aiParams.complexityLevel
      };

      setMessages(prev => [...prev, assistantMessage]);

      // Mettre à jour le sessionId avec le vrai conversation_id du backend (nécessaire pour le quiz)
      if (response.data.conversation_id) {
        setCurrentSessionId(response.data.conversation_id);
      }

      // Sauvegarde dans l'historique avec service
      await historyService.addMessage(
        currentSessionId || response.data.conversation_id,
        messageToSend,
        response.data.response,
        aiParams.complexityLevel,
        'general'
      );

      // Save to local history (seulement pour nouvelle conversation)
      if (!hasExistingConversation) {
        const conversation = {
          id: response.data.conversation_id,
          child_name: selectedChild.name,
          messages: [userMessage, assistantMessage],
          timestamp: new Date().toISOString()
        };
        saveConversation(conversation);
      }

      // Déclencher le prompt PWA après la première réponse IA réussie
      if (!localStorage.getItem('dismaman_first_success')) {
        localStorage.setItem('dismaman_first_success', 'true');
        window.dispatchEvent(new CustomEvent('dismaman:first-success'));
      }

    } catch (error) {
      console.error('Error sending message:', error);
      
      // Fermer le popup de chargement en cas d'erreur
      setShowThinkingPopup(false);
      
      if (error.response?.status === 403) {
        setShowMonetizationPopup(true);
      }
      // SUPPRESSION MESSAGE ERREUR FANTÔME - Ne plus afficher de message d'erreur
    } finally {
      setIsLoading(false);
    }
  };

  const handleFeedbackGiven = async (messageId, feedback) => {
    console.log(`📊 Feedback reçu pour ${selectedChild.name}: ${feedback}`);
    
    // Trouver le message sur lequel le feedback est donné
    const targetMessage = messages.find(msg => msg.messageId === messageId);
    
    if (!targetMessage) {
      console.error(`❌ Message ${messageId} non trouvé`);
      return;
    }
    
    // Marque le message comme ayant reçu un feedback
    setMessages(prev => prev.map(msg => 
      msg.messageId === messageId 
        ? { ...msg, showFeedback: false, feedbackGiven: feedback }
        : msg
    ));

    // Enregistre le feedback dans l'historique
    await historyService.updateMessageFeedback(messageId, feedback);

    // Enregistre le feedback pour l'apprentissage adaptatif
    await adaptiveService.recordFeedback({
      messageId,
      childId: selectedChild.id,
      feedback,
      timestamp: new Date(),
      originalLevel: adaptiveService.getCurrentLevel(selectedChild.id),
      childAge: selectedChild.age,
    });

    // Ici on pourrait envoyer le feedback au backend
    try {
      await api.post('/feedback', {
        message_id: messageId,
        child_id: selectedChild.id,
        feedback: feedback,
        child_age: selectedChild.age
      });
    } catch (error) {
      console.log('Feedback non envoyé au backend:', error);
    }

    // Déterminer le message original pour les feedbacks imbriqués
    let originalMessage = targetMessage;
    let contextMessage = targetMessage.content || targetMessage.role === 'user' ? targetMessage.content : '';
    
    // Si c'est un feedback sur un message de feedback, utiliser le message parent
    if (targetMessage && (targetMessage.isAdditionalInfo || targetMessage.isSimplified)) {
      const parentMessage = messages.find(msg => msg.messageId === targetMessage.parentMessageId);
      if (parentMessage) {
        originalMessage = parentMessage;
        contextMessage = parentMessage.content || ''; // Utiliser le message original comme contexte
      }
    }

    // Si feedback "more_info", générer une réponse supplémentaire
    if (feedback === 'more_info') {
      if (!originalMessage || !contextMessage) {
        console.error('❌ Message original ou contexte manquant pour more_info');
        return;
      }
      
      if (originalMessage) {
        setIsLoading(true);
        
        try {
          const response = await api.post('/chat', {
            message: contextMessage, // Message original de l'enfant
            child_id: selectedChild.id,
            continue_conversation: true,
            feedback_type: "more_info", // Signal backend pour générer plus d'infos
            original_response: originalMessage.content,
            conversation_id: originalMessage.messageId // ID de la conversation spécifique
          });

          const additionalMessage = {
            id: Date.now() + Math.random(),
            role: 'assistant',
            content: response.data.response,
            timestamp: new Date().toISOString(),
            showFeedback: true,
            messageId: response.data.conversation_id,
            isAdditionalInfo: true,
            parentMessageId: messageId // Référence au message parent pour les feedbacks imbriqués
          };

          // Supprimer tout message d'erreur précédent avant d'ajouter la vraie réponse
          setMessages(prev => {
            const filteredMessages = prev.filter(msg => !msg.isError);
            return [...filteredMessages, additionalMessage];
          });

          // Plus besoin de sauvegarde séparée - le backend gère maintenant l'ajout à la conversation existante
        } catch (error) {
          console.error('Erreur lors de la génération d\'informations supplémentaires:', error);
          
          // Ne pas afficher d'erreur générique, réessayer automatiquement une fois
          if (!error.isRetry) {
            console.log('Tentative de réessai automatique...');
            try {
              const retryResponse = await api.post('/chat', {
                message: `Reprenez votre réponse précédente puis ajoutez "Aah tu veux en savoir plus, super !" suivi de détails supplémentaires.`,
                child_id: selectedChild.id,
                continue_conversation: true,
                context: originalMessage.content,
                feedback_type: "more_info",
                original_response: originalMessage.content
              });

              const retryMessage = {
                id: Date.now() + Math.random(),
                role: 'assistant', 
                content: retryResponse.data.response,
                timestamp: new Date().toISOString(),
                showFeedback: true,
                messageId: retryResponse.data.conversation_id,
                isAdditionalInfo: true
              };

              // Supprimer tout message d'erreur avant d'ajouter la vraie réponse
              setMessages(prev => {
                const filteredMessages = prev.filter(msg => !msg.isError);
                return [...filteredMessages, retryMessage];
              });
            } catch (retryError) {
              // Seulement après échec du retry, afficher une erreur discrète
              const errorMessage = {
                id: Date.now() + Math.random(),
                role: 'assistant',
                content: selectedChild.age <= 6 
                  ? "Hmm, j'ai besoin d'un moment pour réfléchir ! 🤔 Peux-tu me reposer ta question ?" 
                  : "Je réfléchis encore à ta question ! Peux-tu la reformuler différemment ?",
                timestamp: new Date().toISOString(),
                showFeedback: false,
                isError: true
              };
              setMessages(prev => [...prev, errorMessage]);
            }
          }
        } finally {
          setIsLoading(false);
        }
      }
      return; // Ne pas afficher de message de remerciement pour more_info
    }

    // Si feedback "too_hard", générer une explication simplifiée
    if (feedback === 'too_hard') {
      if (originalMessage) {
        setIsLoading(true);
        
        try {
          const response = await api.post('/chat', {
            message: contextMessage, // Message original de l'enfant
            child_id: selectedChild.id,
            continue_conversation: true,
            feedback_type: "too_hard", // Signal backend pour simplifier
            original_response: originalMessage.content,
            conversation_id: originalMessage.messageId // ID de la conversation spécifique
          });

          const simplifiedMessage = {
            id: Date.now() + Math.random(),
            role: 'assistant',
            content: response.data.response,
            timestamp: new Date().toISOString(),
            showFeedback: true,
            messageId: response.data.conversation_id,
            isSimplified: true,
            parentMessageId: messageId // Référence au message parent pour les feedbacks imbriqués
          };

          setMessages(prev => [...prev, simplifiedMessage]);

          // Plus besoin de sauvegarde séparée - le backend gère maintenant l'ajout à la conversation existante
        } catch (error) {
          console.error('Erreur lors de la simplification:', error);
        } finally {
          setIsLoading(false);
        }
      }
      return; // Ne pas afficher de message de remerciement pour too_hard
    }

    // Message de remerciement adapté selon le feedback et l'âge (sauf pour more_info)
    let thankYouMessage = '';
    if (feedback === 'too_hard') {
      thankYouMessage = selectedChild.age <= 6 
        ? "Merci ! Je vais essayer d'être plus simple la prochaine fois ! 😊"
        : "Merci pour ton retour ! Je vais adapter mes explications pour toi ! 💪";
    } else if (feedback === 'understood') {
      thankYouMessage = selectedChild.age <= 6
        ? "Bravo ! Tu es très intelligent(e) ! 🌟"
        : "Parfait ! Tu as tout compris ! Continue comme ça ! 🎯";
    }

    // Ajoute le message de remerciement après un petit délai (sauf pour more_info)
    if (thankYouMessage) {
      setTimeout(() => {
        const thankYouMsg = {
          id: Date.now() + Math.random(),
          role: 'assistant',
          content: thankYouMessage,
          timestamp: new Date().toISOString(),
          showFeedback: false,
          isThankYou: true
        };
        setMessages(prev => [...prev, thankYouMsg]);
      }, 1000);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div className="min-h-screen flex flex-col relative overflow-hidden pb-16" style={{ background: 'linear-gradient(135deg, #B8D4E3 0%, #A7D3D8 25%, #9ECDC4 50%, #A8D5BA 75%, #B5DCAE 100%)' }}>
      {/* Gamification Header (stars + streak) */}
      <GamificationHeader />

      {/* Gamification Modals */}
      {weeklyRecap && (
        <WeeklyRecapModal
          recap={weeklyRecap}
          onDismiss={async () => {
            setWeeklyRecap(null);
            const svc = GamificationService.getInstance();
            svc.setApi(api);
            await svc.dismissWeeklyRecap();
          }}
        />
      )}
      {streakMilestone && (
        <StreakMilestoneModal
          milestone={streakMilestone}
          onClose={() => setStreakMilestone(null)}
        />
      )}
      {badgeUnlock && (
        <BadgeUnlockModal
          badge={badgeUnlock}
          onClose={() => setBadgeUnlock(null)}
        />
      )}

      {/* Import Google Font - Caveat */}
      <style>
        {`@import url('https://fonts.googleapis.com/css2?family=Caveat:wght@700&display=swap');`}
      </style>

      {/* Hero zone: bulle + mascotte — dans le flux */}
      <div className="relative w-full h-[200px] sm:h-[260px]">
        {/* Bulle de texte */}
        <div className="absolute left-4 sm:left-8 top-4 sm:top-8 z-20">
          <div className="relative">
            <svg 
              viewBox="0 0 220 90" 
              className="w-[160px] sm:w-[200px] h-auto"
              style={{ filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.15))' }}
            >
              <path 
                d="M 20,45 
                   C 20,20 40,8 80,8 
                   C 120,5 160,10 190,20 
                   C 210,28 215,45 210,60 
                   C 205,75 180,82 140,85 
                   C 100,88 60,85 35,75
                   L 15,88
                   L 25,70
                   C 18,65 20,55 20,45 Z"
                fill="white"
                stroke="#374151"
                strokeWidth="3"
              />
            </svg>
            <p 
              className="absolute top-1/2 left-1/2 text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 whitespace-nowrap"
              style={{ 
                fontFamily: '"Caveat", cursive',
                transform: 'translate(-52%, -45%) rotate(-1deg)'
              }}
            >
              Dis Maman !
            </p>
          </div>
        </div>
        {/* Mascotte */}
        <div className="absolute right-0 top-0 z-0 pointer-events-none">
          <img 
            src="https://customer-assets.emergentagent.com/job_7b59aa73-04f7-469e-b3b0-3b5db031400b/artifacts/0zxv9crx_02D61451-5FF5-40EF-9C50-17F42BA59024.png"
            alt="Alex - Mascotte Dis Maman"
            className="h-[220px] sm:h-[300px] md:h-[360px] object-contain opacity-90"
            style={{ filter: 'drop-shadow(0 10px 25px rgba(0,0,0,0.2))' }}
          />
        </div>
      </div>

      {/* Main Content */}
      <div className="w-full px-3 py-2 flex-1 relative z-10">
        {!selectedChild ? (
          /* PAGE D'ACCUEIL : Question d'abord, puis enfants - ÉPURÉ */
          <div className="space-y-5">
            {/* Toggle Chat / Bibliotheque */}
            <div className="flex items-center justify-center" data-testid="mode-toggle">
              <div className="bg-white/80 backdrop-blur-sm rounded-full p-1 flex shadow-md border border-gray-200">
                <button
                  onClick={() => setViewMode('chat')}
                  className={`px-5 py-2 rounded-full text-sm font-semibold transition-all ${
                    viewMode === 'chat'
                      ? 'bg-[#5BBFBD] text-white shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                  data-testid="mode-toggle-chat"
                >
                  Poser une question
                </button>
                <button
                  onClick={() => setViewMode('library')}
                  className={`px-5 py-2 rounded-full text-sm font-semibold transition-all ${
                    viewMode === 'library'
                      ? 'bg-[#5BBFBD] text-white shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                  data-testid="mode-toggle-library"
                >
                  Biblioth&egrave;que
                </button>
              </div>
            </div>

            {viewMode === 'library' ? (
              <div className="overflow-y-auto -mx-3 px-3 pb-8" style={{ maxHeight: 'calc(100vh - 260px)' }}>
              <LibraryView
                childId={children.length === 1 ? children[0].id : ''}
                children={children}
                api={api}
              />
              </div>
            ) : (
            <>
            {/* Streak + Challenge (gamification) */}
            <div className="space-y-2">
              <WeeklyChallengeDisplay />
            </div>

            {/* Instruction pour les parents */}
            <p className="text-[#333333] font-bold text-sm sm:text-base text-center">
              🤔 Posez la question de votre enfant ici :
            </p>

            {/* ZONE DE TEXTE - Style app mobile avec bordure teal */}
            <div className="relative">
              <textarea
                value={inputMessage}
                onChange={(e) => {
                  if (!canAskQuestion && !isPremium) {
                    setUpgradeChildName(children[0]?.name || '');
                    setShowUpgradeModal(true);
                    return;
                  }
                  setInputMessage(e.target.value);
                }}
                onFocus={() => {
                  if (!canAskQuestion && !isPremium) {
                    setUpgradeChildName(children[0]?.name || '');
                    setShowUpgradeModal(true);
                  }
                }}
                placeholder={
                  !canAskQuestion && !isPremium 
                    ? "Posez une question..."
                    : "Ex: Pourquoi le ciel est bleu ?"
                }
                className={`w-full h-28 p-4 rounded-2xl focus:outline-none resize-none shadow-md ${
                  !canAskQuestion && !isPremium
                    ? 'bg-gray-50/90 text-gray-500 cursor-pointer border-2 border-gray-200'
                    : 'bg-white border-[3px] border-[#5BBFBD] focus:border-[#4A90A4]'
                }`}
                style={{ fontSize: '16px' }}
                disabled={isLoading}
                readOnly={!canAskQuestion && !isPremium}
              />
              
              {!canAskQuestion && !isPremium && (
                <div 
                  className="absolute inset-0 bg-transparent cursor-pointer z-10"
                  onClick={() => {
                    setUpgradeChildName(children[0]?.name || '');
                    setShowUpgradeModal(true);
                  }}
                />
              )}
            </div>

            {/* ENFANTS - Boutons avec gradient (style app mobile) */}
            {children.length > 0 ? (
              <div>
                <p className="text-[#333333] font-bold text-center mb-5"
                   style={{ 
                     fontFamily: '"Caveat", cursive',
                     fontSize: '1.5rem'
                   }}>
                  {!canAskQuestion && !isPremium 
                    ? "Qui pose la question ?"
                    : "Qui pose la question ?"
                  }
                </p>
                
                <div className="flex justify-center items-end gap-6 flex-wrap">
                  {children.map((child, index) => (
                    <button 
                      key={child.id}
                      className={`flex flex-col items-center transition-all duration-200 ${
                        !canAskQuestion && !isPremium
                          ? 'opacity-40 cursor-not-allowed'
                          : !inputMessage.trim() 
                          ? 'opacity-40 cursor-not-allowed' 
                          : 'hover:scale-110 active:scale-95 cursor-pointer'
                      }`}
                      onClick={() => {
                        if (!canAskQuestion && !isPremium) {
                          setUpgradeChildName(child.name);
                          setShowUpgradeModal(true);
                        } else if (inputMessage.trim()) {
                          handleAskForChild(child);
                        }
                      }}
                      disabled={!canAskQuestion && !isPremium || !inputMessage.trim()}
                    >
                      {/* Bouton avec gradient - Style app mobile */}
                      <div 
                        className={`w-16 h-16 rounded-full flex items-center justify-center shadow-md text-white
                          ${inputMessage.trim() && !(!canAskQuestion && !isPremium) ? 'hover:shadow-lg' : ''}
                        `}
                        style={{ 
                          background: index % 2 === 0 
                            ? 'linear-gradient(135deg, #4A90D9 0%, #5BBFBD 100%)'
                            : 'linear-gradient(135deg, #4ECDC4 0%, #45B7AA 100%)'
                        }}
                      >
                        <svg 
                          viewBox="0 0 24 24" 
                          className="w-8 h-8 fill-current"
                          style={{ transform: 'rotate(-15deg)' }}
                        >
                          <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
                        </svg>
                      </div>
                      {/* Prénom en police Caveat */}
                      <span 
                        className="mt-2 text-[#333333] font-semibold"
                        style={{ 
                          fontFamily: '"Caveat", cursive',
                          fontSize: '1.4rem'
                        }}
                      >
                        {child.name}
                      </span>
                    </button>
                  ))}
                </div>
                
                {!inputMessage.trim() && !(!canAskQuestion && !isPremium) && (
                  <p className="text-gray-600 text-sm mt-4 text-center"
                     style={{ textShadow: '0 1px 2px rgba(255,255,255,0.5)' }}>
                    Veuillez saisir une question avant de la poser
                  </p>
                )}
              </div>
            ) : (
              /* Interface nouveau utilisateur sans enfant : champ prénom + bouton envoi */
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <input
                    type="text"
                    value={pendingChildName}
                    onChange={e => setPendingChildName(e.target.value)}
                    placeholder="Prénom de votre enfant"
                    className="flex-1 px-4 py-3 rounded-xl border-[3px] border-[#5BBFBD] focus:border-[#4A90A4] focus:outline-none bg-white"
                    style={{ fontSize: '16px', fontFamily: '"Caveat", cursive' }}
                    data-testid="onboarding-child-name"
                  />
                  <button
                    onClick={() => {
                      if (inputMessage.trim() && pendingChildName.trim()) {
                        setShowDemoChildPopup(true);
                      }
                    }}
                    disabled={!inputMessage.trim() || !pendingChildName.trim()}
                    className={`w-14 h-14 rounded-full flex items-center justify-center shadow-md text-white transition-all ${
                      inputMessage.trim() && pendingChildName.trim()
                        ? 'hover:scale-110 active:scale-95 cursor-pointer'
                        : 'opacity-40 cursor-not-allowed'
                    }`}
                    style={{ 
                      background: inputMessage.trim() && pendingChildName.trim()
                        ? 'linear-gradient(135deg, #4A90D9 0%, #5BBFBD 100%)'
                        : '#ccc',
                      animation: inputMessage.trim() && pendingChildName.trim() ? 'breathe 2s ease-in-out infinite' : 'none'
                    }}
                    data-testid="onboarding-send-btn"
                  >
                    <svg viewBox="0 0 24 24" className="w-7 h-7 fill-current" style={{ transform: 'rotate(-15deg)' }}>
                      <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
                    </svg>
                  </button>
                </div>
                {(!inputMessage.trim() || !pendingChildName.trim()) && (
                  <p className="text-gray-500 text-sm text-center">
                    {!inputMessage.trim() && !pendingChildName.trim()
                      ? 'Saisissez une question et le prénom de votre enfant'
                      : !inputMessage.trim()
                      ? 'Saisissez la question de votre enfant ci-dessus'
                      : 'Saisissez le prénom de votre enfant'
                    }
                  </p>
                )}
              </div>
            )}

            {/* BOUTON AJOUTER ENFANT - Visible uniquement si l'utilisateur a déjà des enfants */}
            {children.length > 0 && (
            <div className="text-center">
              <button
                onClick={() => setShowAddChildModal(true)}
                className="px-6 py-3 rounded-2xl transition-all duration-200
                  bg-white/80 border-2 border-dashed border-[#5BBFBD]/50
                  hover:bg-white hover:border-[#5BBFBD] hover:shadow-md
                  text-[#4A90A4] active:scale-95"
                style={{ fontFamily: '"Caveat", cursive', fontSize: '1.2rem' }}
              >
                + Ajouter un enfant
              </button>
            </div>
            )}
            </>
            )}
          </div>
        ) : (
          /* Interface de chat - Sans header dupliqué */
          <div className="space-y-4">
            
            {/* En-tête avec nom de l'enfant et bouton nouvelle conversation */}
            <div className="flex items-center justify-between mb-4 px-1">
              <button
                onClick={() => {
                  setSelectedChild(null);
                  setMessages([]);
                }}
                className="text-[#4A90A4] text-sm font-medium hover:text-[#3A7A8A] transition-colors flex items-center gap-1"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Retour
              </button>
              
              <h2 
                className="text-lg sm:text-xl font-bold text-[#333333]"
                style={{ fontFamily: '"Caveat", cursive' }}
              >
                💬 {selectedChild.name}
              </h2>
              
              <button
                onClick={() => {
                  setMessages([]);
                  setInputMessage('');
                }}
                className="text-[#4A90A4] text-sm font-medium hover:text-[#3A7A8A] transition-colors"
              >
                + Nouveau
              </button>
            </div>

            {/* Zone des messages - directement sur le fond, marges réduites */}
            <div className="space-y-4 mb-4 max-h-[60vh] overflow-y-auto px-1">
              <div ref={messagesEndRef} />
              {messages.map((message, index) => (
                <div key={message.id} className="w-full">
                  {/* Question de l'enfant - Bulle cyan/teal (style app mobile) */}
                  {message.role === 'user' && (
                    <div className="flex justify-start">
                      <div 
                        className="max-w-[85%] px-4 py-3 rounded-2xl shadow-md text-white"
                        style={{ 
                          background: message.isFollowUp 
                            ? 'linear-gradient(135deg, #7ECFCD 0%, #8DD5D3 100%)' 
                            : 'linear-gradient(135deg, #4FBDBA 0%, #5BBFBD 100%)' 
                        }}
                      >
                        <p className="text-base leading-relaxed whitespace-pre-wrap">{message.content}</p>
                        <p className="text-xs mt-2 text-white/70">
                          {new Date(message.timestamp).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    </div>
                  )}
                  
                  {/* Réponse IA - Bulle blanche (style app mobile) */}
                  {message.role === 'assistant' && (
                    <div className="flex justify-end">
                      <div className={`max-w-[85%] px-4 py-3 rounded-2xl shadow-md ${
                        message.isThankYou
                          ? 'bg-[#E8F5E9] border border-[#4CAF50]/30 text-[#2E7D32]'
                          : message.isError
                          ? 'bg-[#FFF3E0] border border-[#FFAB91] text-[#E64A19]'
                          : 'bg-white text-[#333333]'
                      }`}>
                        <p className="text-base leading-relaxed whitespace-pre-wrap">{message.content}</p>
                        <p className="text-xs mt-2 text-[#999999]">
                          {new Date(message.timestamp).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    </div>
                  )}
                  
                  {/* Boutons de feedback après les réponses IA */}
                  {message.showFeedback && message.role === 'assistant' && !message.feedbackGiven && (
                    <div className="flex justify-end mt-2">
                      <FeedbackButtons
                        messageId={message.messageId}
                        childAge={selectedChild.age}
                        childId={selectedChild?.id}
                        conversationId={currentSessionId}
                        onFeedbackGiven={(feedback) => handleFeedbackGiven(message.messageId, feedback)}
                      />
                    </div>
                  )}
                  
                  {/* Bannière douce post-réponse (utilisateurs gratuits, après chaque réponse IA) */}
                  {message.role === 'assistant' && !message.isThankYou && !message.isError && !message.isFollowUp && !isPremium && (
                    <div 
                      data-testid="post-response-upgrade-banner"
                      className="flex justify-end mt-2"
                    >
                      <div 
                        className="max-w-[85%] rounded-2xl p-4"
                        style={{ 
                          background: 'linear-gradient(135deg, #F0F9FF 0%, #E0F2FE 100%)',
                          border: '1px solid #BAE6FD'
                        }}
                      >
                        <p className="text-gray-700 text-sm font-semibold mb-1" style={{ fontFamily: '"Caveat", cursive', fontSize: '1.1rem' }}>
                          {selectedChild?.name 
                            ? `🌟 ${selectedChild.name} a encore des questions !`
                            : `🌟 Votre enfant a encore des questions !`
                          }
                        </p>
                        <p className="text-gray-600 text-sm leading-relaxed mb-3">
                          Passez à Explorateur pour répondre à tous ses "pourquoi"… sans limite et sans jamais le laisser sans réponse.
                        </p>
                        <button
                          onClick={() => navigate('/subscription')}
                          data-testid="post-response-upgrade-cta"
                          className="w-full py-2.5 rounded-full font-semibold text-white text-sm transition-all hover:opacity-90 active:scale-[0.98]"
                          style={{ background: 'linear-gradient(135deg, #4A90A4 0%, #5BBFBD 100%)' }}
                        >
                          Découvrir Explorateur
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Affichage après feedback donné - Bouton Quiz */}
                  {message.feedbackGiven && (
                    <div className="flex flex-col items-end mt-2 gap-2">
                      <div className="text-xs text-teal-600 bg-white/60 backdrop-blur-sm px-3 py-1 rounded-full">
                        {message.feedbackGiven === 'understood' ? '👍 Compris !' :
                         message.feedbackGiven === 'too_hard' ? '🤔 Un peu difficile' :
                         '🧐 Je veux en savoir plus'}
                      </div>
                      {/* Bouton Lancer le quiz */}
                      {currentSessionId && selectedChild?.id && (
                        <button
                          onClick={() => navigate(`/quiz?conversation=${currentSessionId}&child=${selectedChild.id}`)}
                          className="flex items-center gap-2 px-5 py-3 rounded-full
                            text-white font-semibold shadow-md
                            hover:opacity-90 transition-all duration-200 active:scale-95"
                          style={{ background: 'linear-gradient(135deg, #9B59B6 0%, #8E44AD 100%)' }}
                        >
                          <span className="text-lg">❓</span>
                          <span>Lancer le quiz</span>
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))}
              
              {isLoading && (
                <div className="flex justify-end">
                  <div className="bg-white/80 backdrop-blur-sm px-4 py-3 rounded-2xl shadow-lg">
                    <div className="flex items-center space-x-2">
                      <div className="flex space-x-1">
                        <div className="w-2 h-2 bg-teal-400 rounded-full animate-bounce"></div>
                        <div className="w-2 h-2 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                        <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                      </div>
                      <span className="text-sm text-gray-500">Je réfléchis...</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Zone de saisie - Style app mobile avec bordure teal */}
            <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-md p-3">
              <div className="flex space-x-2">
                <textarea
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={`Nouvelle question pour ${selectedChild.name}...`}
                  className="flex-1 resize-none border-[3px] border-[#5BBFBD] rounded-xl px-3 py-2 focus:outline-none focus:border-[#4A90A4] bg-white transition-colors"
                  style={{ fontSize: '16px' }}
                  rows={2}
                  disabled={isLoading}
                />
                <button
                  onClick={handleSendMessage}
                  disabled={!inputMessage.trim() || isLoading}
                  className={`w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-200 ${
                    !inputMessage.trim() || isLoading
                      ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      : 'text-white shadow-md hover:shadow-lg hover:scale-105'
                  }`}
                  style={{ 
                    background: !inputMessage.trim() || isLoading 
                      ? undefined 
                      : 'linear-gradient(135deg, #4A90A4 0%, #6BB5A2 100%)' 
                  }}
                >
                  {isLoading ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  ) : (
                    <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current" style={{ transform: 'rotate(-15deg)' }}>
                      <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
                    </svg>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      <AddChildModal
        isOpen={showAddChildModal}
        onClose={() => setShowAddChildModal(false)}
        onAddChild={handleAddChild}
        onUpgrade={onNavigateToSubscription}
      />

      <ChildrenManagementModal
        isOpen={showChildSelection}
        onClose={() => setShowChildSelection(false)}
        children={children}
        onSelectChild={handleSelectChild}
        onAddChild={() => {
          setShowChildSelection(false);
          setShowAddChildModal(true);
        }}
        onDeleteChild={handleDeleteChild}
      />

      <MonetizationPopup
        isOpen={showMonetizationPopup}
        onClose={() => setShowMonetizationPopup(false)}
        trialDaysRemaining={0}
        onUpgrade={(selectedPlan) => {
          console.log('🚀 Upgrade to premium requested with plan:', selectedPlan);
          
          // Fermer le popup de monétisation
          setShowMonetizationPopup(false);
          
          // Rediriger vers la page d'abonnement avec le plan pré-sélectionné
          onNavigateToSubscription(selectedPlan);
        }}
      />

      {/* NOUVEAU : Popup de réflexion pendant que l'IA génère */}
      <ThinkingPopup
        isOpen={showThinkingPopup}
        childName={selectedChild?.name}
        childAge={selectedChild?.age}
        question={inputMessage}
      />

      {/* PWA Install Prompt */}
      <PWAInstallPrompt />

      {/* Popup rassurant pour créer le premier enfant */}
      <ReassuringChildPrompt
        isOpen={showReassuringPrompt}
        onClose={() => setShowReassuringPrompt(false)}
        onChildCreated={handleChildCreatedFromPrompt}
        api={api}
        onUpgrade={onNavigateToSubscription}
      />

      {/* Popup léger de création enfant (démo OU nouveau flow inline) */}
      {showDemoChildPopup && <DemoChildPopup
        childName={pendingChildName || demoData?.childName || ''}
        onSubmit={handleDemoChildCreated}
        onClose={() => setShowDemoChildPopup(false)}
      />}
      
      {/* Modal de chargement question - Style app mobile */}
      <LoadingQuestionModal
        isVisible={isLoading}
        childName={selectedChild?.name}
        question={currentQuestion}
      />

      {/* Modal d'upgrade chaleureux (remplace l'ancien alert 🚫) */}
      {showUpgradeModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-5">
          <div 
            data-testid="upgrade-modal"
            className="bg-white rounded-3xl p-7 max-w-sm w-full shadow-2xl text-center"
            style={{ animation: 'fadeIn 0.25s ease-out' }}
          >
            <div className="text-4xl mb-4">🌟</div>
            <h2 
              className="text-2xl font-bold text-[#333] mb-3"
              style={{ fontFamily: "'Caveat', cursive" }}
            >
              {upgradeChildName 
                ? `${upgradeChildName} a encore des questions !`
                : 'Votre enfant a encore des questions !'
              }
            </h2>
            <p className="text-gray-600 text-sm leading-relaxed mb-6">
              Passez à Explorateur pour répondre à tous ses "pourquoi"… sans limite et sans jamais le laisser sans réponse.
            </p>
            <button
              onClick={() => {
                setShowUpgradeModal(false);
                navigate('/subscription');
              }}
              data-testid="upgrade-modal-cta"
              className="w-full py-3.5 rounded-full font-bold text-white text-base transition-all hover:opacity-90 active:scale-[0.98] mb-3"
              style={{ background: 'linear-gradient(135deg, #4A90A4 0%, #5BBFBD 100%)' }}
            >
              Découvrir Explorateur
            </button>
            <button
              onClick={() => setShowUpgradeModal(false)}
              data-testid="upgrade-modal-dismiss"
              className="text-gray-400 text-sm hover:text-gray-500 transition-colors"
            >
              Pas maintenant
            </button>
          </div>
        </div>
      )}
      
      {/* Menu flottant */}
      <FloatingMenu />
      
      {/* Footer avec déconnexion */}
      <Footer onLogout={logout} />
    </div>
  );
};

export default ChatPage;